/** 从产品分类文案生成更丰富的「特点展示」内容 */

const SPEC_LABELS = {
  system: ['核心能力', '功能范围', '应用场景'],
  education: ['技术参数', '结构配置', '安全与适用'],
  educationShort: ['技术参数', '实验项目', '适用场景'],
  robotics: ['核心能力', '功能范围', '应用场景'],
  agv: ['核心能力', '功能范围', '应用场景'],
  automation: ['核心能力', '功能范围', '应用场景'],
}

const DESC_FRAMES = {
  system: [
    (text) =>
      `${text} 高仁科技以企业实际业务流程为基础，提供从需求调研、方案设计到本地化部署的完整交付，帮助制造企业打通计划、生产、仓储与协同管理链路。`,
    (text) =>
      `${text} 可结合数据采集、可视化看板与设备联网能力，支撑产线透明化管理和经营决策，并支持按模块分期建设与后续扩展。`,
    (text) =>
      `${text} 适用于离散制造、能源管理、智慧工厂升级及院校示范教学等场景，可根据行业工艺和组织架构灵活定制。`,
  ],
  education: [
    (text) =>
      `${text} 装置采用工业级结构设计，满足院校实训、考核评估与竞赛备赛等教学需求，可与课程体系和实训室建设方案配套交付。`,
    (text) =>
      `${text} 支持模块化组合与实验项目拓展，便于开展从基础认知到综合应用的阶梯式教学，适合实训基地与示范教室部署。`,
    (text) =>
      `${text} 符合相关安全规范，适合机电、自动化、机器人及工业互联网等专业方向的实践教学与技能训练。`,
  ],
  robotics: [
    (text) =>
      `${text} 高仁科技提供整机定制、控制集成与二次开发支持，可面向展厅、园区、实验室及工业现场等多类场景交付。`,
    (text) =>
      `${text} 支持视觉、语音、导航与任务调度等能力扩展，可与上位系统、云平台及教学平台对接，满足展示与实用双重需求。`,
    (text) =>
      `${text} 适用于服务展示、巡检运维、科研验证与职业教育实训等场景，支持按需配置载荷与软件功能。`,
  ],
  agv: [
    (text) =>
      `${text} 覆盖方案设计、调度软件开发、现场调试与运维支持，可与 WMS、MES 及产线设备形成物流协同。`,
    (text) =>
      `${text} 支持多机调度、路径规划、任务下发与异常处理，适配仓储搬运、柔性制造及场内复杂路径应用。`,
    (text) =>
      `${text} 适用于制造车间、仓储中心、物流转运及教学示范等场景，支持潜伏、叉取、复合等多种形态组合。`,
  ],
  automation: [
    (text) =>
      `${text} 从工艺分析、方案设计到设备制造与调试验收，高仁科技提供非标自动化整线或工位级定制开发服务。`,
    (text) =>
      `${text} 可集成机器人、视觉、传感器、输送线与工业软件，实现柔性生产、信息化联动与节拍优化。`,
    (text) =>
      `${text} 适用于装配、检测、包装、上下料及产线改造等场景，支持效果图深化与空间规划配合。`,
  ],
}

function splitEnumerations(text) {
  const match = text.match(/(?:支持|涵盖|包括|覆盖|提供|含|含)([^，。；]+)/)
  if (!match) return []

  const segment = match[1]
  const parts = segment
    .split(/[、，,/及与和]/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 2 && s.length <= 24)

  if (parts.length < 2) return []
  return parts.slice(0, 6).map((part) => `覆盖 ${part.replace(/等.*$/, '')} 相关能力`)
}

function extractTags(text, specType) {
  const known = {
    system: ['MES', 'ERP', 'WMS', 'OA', '数字孪生', '工业互联网', '节能管理', '光伏', '风电', '点检'],
    education: ['实训', 'PLC', '传感器', '电工', '竞赛', '数字教材', '资源库'],
    robotics: ['人型机器人', '机器狗', 'AI', '无人机', '视觉', '巡检'],
    agv: ['AGV', 'AMR', 'SLAM', '调度', '叉车', '复合机器人'],
    automation: ['非标产线', '自动化', '工位', '视觉', '输送线'],
  }

  const pool = known[specType] || known.system
  const found = pool.filter((tag) => text.includes(tag.replace(/\+.*/, '')))

  if (found.length) return found.slice(0, 5)

  const split = splitEnumerations(text)
  return split
    .map((s) => s.replace(/^覆盖\s*/, '').replace(/\s*相关能力$/, ''))
    .slice(0, 4)
}

function expandBullets(text, sectionIndex) {
  if (!text) return []

  const items = [text]
  const derived = splitEnumerations(text)

  if (derived.length) {
    items.push(...derived)
  } else if (sectionIndex === 0) {
    items.push('支持按项目需求进行方案论证、功能定制与分期实施')
  } else if (sectionIndex === 1) {
    items.push('可与现有设备、业务系统及云平台对接，形成数据联动')
  } else {
    items.push('提供咨询、实施、培训与后续运维支持服务')
  }

  return [...new Set(items)].slice(0, 5)
}

function buildSectionDesc(text, config, sectionIndex) {
  const specType = config.specType || 'system'
  const isEquipment =
    specType === 'education' && config.detailMode === 'equipment'
  const key = isEquipment ? 'education' : specType
  const frames = DESC_FRAMES[key] || DESC_FRAMES.system
  const frame = frames[sectionIndex] || frames[0]
  return frame(text)
}

function resolveSpecType(config) {
  if (config.specType === 'education' && config.detailMode === 'equipment') {
    return config.featureBullets?.length === 3 ? 'educationShort' : 'education'
  }
  return config.specType || 'system'
}

export function enrichFeatureSection(feat, bullet, config, sectionIndex) {
  const specType = resolveSpecType(config)
  const bullets = bullet ? expandBullets(bullet, sectionIndex, specType) : []
  const tags = bullet ? extractTags(bullet, specType === 'educationShort' ? 'education' : specType) : []

  return {
    ...feat,
    desc: bullet
      ? feat.desc || buildSectionDesc(bullet, config, sectionIndex)
      : feat.desc || '',
    bullets: feat.bullets?.length ? feat.bullets : bullets,
    tags: feat.tags?.length ? feat.tags : tags,
  }
}

export function enrichFeatureSections(sections, config) {
  return sections.map((section, index) =>
    enrichFeatureSection(section, config.featureBullets?.[index] || section.bullets?.[0] || '', config, index)
  )
}

export { SPEC_LABELS }
